import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { RfqItem, RfqOutputData, RfqOutputService } from './rfq-output.service';
import { MatDialog } from '@angular/material/dialog';
import {
  firstValueFrom,
  lastValueFrom,
  skip,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import {
  RfqDeadlineDialogComponent,
  RfqDeadlineDialogResult,
} from '../rfq-deadline-dialog/rfq-deadline-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ExtendedKeycloakUser,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import moment, { Moment } from 'moment';
import { MatPaginator } from '@angular/material/paginator';
import {
  BodyGetRFQDocument,
  OutputHistories,
  PrintDocument,
} from 'src/app/shared/models/database-models';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { orderBy } from 'lodash';
import { FileService } from 'src/app/shared/services/file.service';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { MatTableDataSource } from '@angular/material/table';
import { PurchaseExecutives } from 'src/app/shared/models/database-models/PurchaseExecutives.model';
import { rfqListDropDown } from 'src/app/shared/models/app-models/DropDown.model';

interface RFQData {
  Version: string;
  RFQNumber: string;
  PurchaseExecutive: string;
  RFQDeadline: Date;
  ItemNumber: string;
  VendorName: string;
  LastEmailPrinted: Date | null;
  data: {
    initialSentDate: Date | null;
    newRFQDeadline?: Date | null;
    uploadPaths: string[];
    isRfqSummaryRaised: boolean;
  };
}

@Component({
  selector: 'app-rfq-output',
  templateUrl: './rfq-output.component.html',
  styleUrls: ['./rfq-output.component.scss'],
})
export class RfqOutputComponent implements OnInit, OnDestroy {
  MPDocumentNumber?: string;
  rfqDeadlineDialogSubscription?: Subscription;
  rfqOutputServiceSubscription?: Subscription;
  currentRoute = ['Purchase Modes'];
  breadCrumbFlow: string[] = ['Purchase Modes'];
  currentPageString: string = 'RFQ output';
  activePage: string = 'RFQ output';
  purchaseType: string = '';
  KCUserList: ExtendedKeycloakUser[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  RFQDisplayColumns = [
    'Version',
    'RFQNumber',
    'PurchaseExecutive',
    'RFQDeadline',
    'LastEmailPrinted',
    'ItemNumber',
    'VendorName',
    'Action',
  ];
  OutputHistoryDisplayColumns = [
    'VersionNumber',
    'Type',
    'RFQNumber',
    'Name',
    'Email',
    'ModifiedOn',
    'RFQDeadline',
    'CreatedBy',
    'TempStatus',
    'TempReason',
  ];
  defaultPageSize: number = 10;
  pageSizeOptions: number[] = [5, 10, 20];
  rfqDataSource: MatTableDataSource<RFQData> = new MatTableDataSource<RFQData>(
    []
  );
  form!: MyPurchaseForm;
  amIaAssignedPe: boolean = false
  loggedInUser: string | null = null;
  isRFQDeadlineEditable: boolean = false;
  outputHistoryDataSource: MatTableDataSource<OutputHistories> =
    new MatTableDataSource<OutputHistories>([]);
  private rfqPaginator!: MatPaginator;
  private outputHistoryPaginator!: MatPaginator;
  isUserPE: boolean = false;
  private rfqSummaryDetails: rfqListDropDown[] | null = null;
  private rfqItems: RfqItem[] | undefined = [];

  // ref #rfqPaginator (using html id)
  // Use this method instead of AfterViewInit because *ngIf conditionally renders mat-table and mat-paginator
  // mat-paginator needs to be in view before it's ref can be attached to the mat-table
  @ViewChild('rfqPaginator', { read: MatPaginator }) set matRFQPaginator(
    mp: MatPaginator
  ) {
    this.rfqPaginator = mp;
    this.rfqDataSource.paginator = this.rfqPaginator;
  }
  @ViewChild('outputHistoryPaginator', { read: MatPaginator })
  set matOutputHistoryPaginator(mp: MatPaginator) {
    this.outputHistoryPaginator = mp;
    this.outputHistoryDataSource.paginator = this.outputHistoryPaginator;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private backendApi: BackendApiService,
    public rfqOutputService: RfqOutputService,
    public rfqDeadlineDialog: MatDialog,
    private _snackBar: MatSnackBar,
    private fileService: FileService,
    
    private store: Store<{ dp: StoreDropdowns ,form: MyPurchaseForm,  user: UserInformation;}>
  ) {
    const previousPageName = sessionStorage.getItem('fromPage');
    if (previousPageName) {
      this.breadCrumbFlow = [previousPageName];
    } 
    const MPDocumentNumber = this.route.snapshot.params['id'].split(';')[0] as string;
    if (MPDocumentNumber) {
      this.MPDocumentNumber = MPDocumentNumber;
      this.currentRoute.push(MPDocumentNumber as string);
      this.breadCrumbFlow.push(MPDocumentNumber as string);
    }
    this.purchaseType = this.route.snapshot.params['purchaseType'].split(
      ';'
    )[0] as string;

    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (dp: StoreDropdowns) => {
        this.KCUserList = dp.UserList as ExtendedKeycloakUser[];
        
        if (!Array.isArray(dp.UserList) || dp.UserList.length === 0) {
          return;
        }

        const rfqOutputData = this.rfqOutputService.rfqOutputData;

        // Fetch RFQ Outputs from backend
        // Output History data requires mapping to UserList, hence dp.UserList needs to be available before fetching RfqOutput
        if (
          this.MPDocumentNumber &&
          (!rfqOutputData ||
            !Array.isArray(rfqOutputData.RFQ) ||
            rfqOutputData.RFQ.length === 0 ||
            rfqOutputData.RFQ[0].MPDocumentNumber !== this.MPDocumentNumber)
        ) {
          await this.rfqOutputService.onFetchRfqOutput(this.MPDocumentNumber);
        }
      });

      this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.loggedInUser = user.userName;
        if (this.loggedInUser) {
          this.loadGetPurchaseExecutivesList();
        }
      });
      this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        this.form = form;
          this.amIaAssignedPe = !!this.form.ItemList.find(x => x.AddItems.PurchaseExecutive == this.loggedInUser)
        if (this.amIaAssignedPe) {
          if (form.documentStatusId == 9 && form.documentSubStatusId == 25) this.isRFQDeadlineEditable = true;
        }
        if (this.form) {
          this.getRFQSummaryDetails();
        }
      });

    // Update material table data if data from service observable changes
    this.rfqOutputServiceSubscription =
      this.rfqOutputService.rfqOutput$.subscribe({
        next: (rfqOutput): void => {
          this.rfqItems = rfqOutput?.data?.RFQ;
          const outputHistories = rfqOutput?.data?.outputHistories;

          // Output History
          let outputHistoryData: OutputHistories[] = [];
          if (Array.isArray(outputHistories) && outputHistories.length > 0) {
            outputHistoryData =
              this.formatOutputHistoryDataSource(outputHistories);
          }

          this.outputHistoryDataSource =
            new MatTableDataSource<OutputHistories>(outputHistoryData);
        },
      });
  }

  async ngOnInit(): Promise<void> {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    if (this.rfqDeadlineDialogSubscription) {
      this.rfqDeadlineDialogSubscription.unsubscribe();
    }

    if (this.rfqOutputServiceSubscription) {
      this.rfqOutputServiceSubscription.unsubscribe();
    }
  }

  hasRFQChanges(rfqItems?: RfqOutputData['RFQ']) {
    return (
      Array.isArray(rfqItems) &&
      rfqItems.some((rfqItem) => !!rfqItem.newRFQDeadline)
    );
  }

  async getRFQSummaryDetails() {
    try {
      const res = await lastValueFrom(this.backendApi.getRFQNumbers(this.form.documentNo!));
      this.rfqSummaryDetails = res;
      // RFQ
      let rfqData: RFQData[] = [];
      if (Array.isArray(this.rfqItems) && this.rfqItems.length > 0) {
        rfqData = this.formatRFQDataSource(this.rfqItems);
      }
      this.rfqDataSource = new MatTableDataSource<RFQData>(rfqData);
    }
    catch (error) {
      console.log(error);
    }
  }

  formatRFQDataSource(rawData: RfqOutputData['RFQ']) {
    const rfqNumberToData: {
      [key: string]: {
        Version: string;
        RFQNumber: string;
        PurchaseExecutive: string;
        RFQDeadline: Date;
        ItemNumbers: Set<Number>;
        VendorNames: Set<string>;
        uploadPaths: Set<string>;
        maxOutputHistoryTime: Moment | null;
        minOutputHistoryTime: Moment | null;
        newRFQDeadline?: Date;
        isRfqSummaryRaised: boolean;
      };
    } = {};

    // Group data by RFQNumber
    for (const doc of rawData) {
      const {
        RFQNumber,
        RFQItems,
        outputHistories,
        vendor,
        Version,
        RFQDeadline,
        newRFQDeadline,
        UploadPath,
      } = doc;

      const maxOutputHistoryTime = outputHistories
        ? moment.max(
            outputHistories
              .filter(
                (outputHistory) => outputHistory.VersionNumber === Version
              )
              .map((outputHistory) => moment(outputHistory.CreatedOn))
          )
        : null;
      const minOutputHistoryTime = outputHistories
        ? moment.min(
            outputHistories.map((outputHistory) =>
              moment(outputHistory.CreatedOn)
            )
          )
        : null;

      const itemNumbers =
        RFQItems?.map((rfqItem) => rfqItem.PurchaseItemNumber) ?? [];
      const vendorName = vendor?.Name;

      const rfqSummary = this.rfqSummaryDetails?.find(item => item.RFQNumber === RFQNumber);

      if (!rfqNumberToData[RFQNumber]) {
        // If the RFQNumber does not exist in the data, initialize it
        rfqNumberToData[RFQNumber] = {
          Version: Version.toString(),
          RFQNumber,
          RFQDeadline,
          ItemNumbers: new Set(itemNumbers),
          VendorNames: new Set(vendorName ? [vendorName] : []),
          maxOutputHistoryTime: maxOutputHistoryTime,
          minOutputHistoryTime: minOutputHistoryTime,
          newRFQDeadline,
          uploadPaths: new Set(UploadPath ? [UploadPath] : []),
          PurchaseExecutive: 'Not Applicable', 
          isRfqSummaryRaised: false, 
        };
      
        if (rfqSummary) {
          rfqNumberToData[RFQNumber].PurchaseExecutive = rfqSummary.PE
            ? (this.KCUserList.find((user) => user.id == rfqSummary.PE)?.last_name || '-')
            : 'Not Applicable';
          rfqNumberToData[RFQNumber].isRfqSummaryRaised = !!rfqSummary.RFQSummaryNumber;
        }

        continue;
      }
      // Grouped and min/max fields

      // Add to ItemNumbers set
      rfqNumberToData[RFQNumber].ItemNumbers = new Set([
        ...rfqNumberToData[RFQNumber].ItemNumbers,
        ...itemNumbers,
      ]);

      // Add to VendorNames set
      if (vendorName) {
        rfqNumberToData[RFQNumber].VendorNames.add(vendorName);
      }

      // Store max time
      if (
        maxOutputHistoryTime &&
        (!rfqNumberToData[RFQNumber].maxOutputHistoryTime ||
          maxOutputHistoryTime.isAfter(
            rfqNumberToData[RFQNumber].maxOutputHistoryTime
          ))
      ) {
        rfqNumberToData[RFQNumber].maxOutputHistoryTime = maxOutputHistoryTime;
      }

      // Store min time
      if (
        minOutputHistoryTime &&
        (!rfqNumberToData[RFQNumber].minOutputHistoryTime ||
          minOutputHistoryTime.isBefore(
            rfqNumberToData[RFQNumber].minOutputHistoryTime
          ))
      ) {
        rfqNumberToData[RFQNumber].minOutputHistoryTime = minOutputHistoryTime;
      }

      // Add to uploadPaths set
      if (UploadPath) {
        rfqNumberToData[RFQNumber].uploadPaths.add(UploadPath);
      }
    }

    const dataSourceData = [];
    // Format to table row
    for (const [RFQNumber, data] of Object.entries(rfqNumberToData)) {
      dataSourceData.push({
        Version: data.Version,
        RFQNumber: data.RFQNumber,
        PurchaseExecutive: data.PurchaseExecutive,
        RFQDeadline: data.RFQDeadline,
        ItemNumber: Array.from(data.ItemNumbers).join(', '),
        VendorName: Array.from(data.VendorNames).join(', '),
        LastEmailPrinted: data.maxOutputHistoryTime?.toDate() ?? null,
        data: {
          initialSentDate: data.minOutputHistoryTime?.toDate() ?? null,
          newRFQDeadline: data.newRFQDeadline,
          uploadPaths: Array.from(data.uploadPaths),
          isRfqSummaryRaised: data.isRfqSummaryRaised
        },
      });
    }

    return dataSourceData;
  }

  formatOutputHistoryDataSource(rawData: RfqOutputData['outputHistories']) {
    const KCUserList = this.KCUserList;

    // Map CreatedBy username to full name
    const mappedData = rawData.map((output) => {
      return {
        ...output,
        CreatedBy:
          KCUserList.find((user) => user.id == output.CreatedBy)?.last_name ??
          output.CreatedBy,
      };
    });

    const dataSourceData = orderBy(
      mappedData,
      [(obj) => new Date(obj.ModifiedOn)],
      ['desc']
    );
    return dataSourceData;
  }

  formatHeader(camelCaseHeader: string): string {
    const words = camelCaseHeader
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, function (str) {
        return str.toUpperCase();
      });
    return words;
  }

  // Only allowed to edit if current RFQDeadline has not yet passed
  isAllowEditRFQDeadline(currentRFQDeadline: Date, isRFQSummaryRaised: boolean) {
    if (
      !isRFQSummaryRaised && (this.form.documentStatusId == 9 && this.form.documentSubStatusId == 25) &&
      (
        (
          !this.form.isRFS &&
          (
            this.form?.generalForm?.RO?.id === this.loggedInUser ||
            this.form.createdBy === this.loggedInUser
          )
        ) || this.isUserPE
      )
    ) {
      return !moment().isBefore(moment(currentRFQDeadline));
    } else {
      return true;
    }
  }

  onEditRFQDeadline({
    RFQNumber,
    initialSentDate,
    RFQDeadline,
    newRFQDeadline,
  }: {
    RFQNumber: string;
    initialSentDate: Date;
    RFQDeadline: Date;
    newRFQDeadline?: Date;
  }) {
    if (this.rfqDeadlineDialogSubscription) {
      this.rfqDeadlineDialogSubscription.unsubscribe();
    }

    // Open dialog
    const rfqDeadlineDialogRef = this.rfqDeadlineDialog.open(
      RfqDeadlineDialogComponent,
      {
        width: '600px',
        data: {
          rfqNumber: RFQNumber,
          initialSentDate: initialSentDate,
          rfqDeadline: RFQDeadline,
          newRFQDeadline: newRFQDeadline,
        },
      }
    );

    // After dialog is closed
    this.rfqDeadlineDialogSubscription = rfqDeadlineDialogRef
      .afterClosed()
      .subscribe((result: RfqDeadlineDialogResult) => {
        if (
          result &&
          result.dialogForm &&
          result.dialogData &&
          result.dialogForm.newRFQDeadline
        ) {
          // Store and update newRFQDeadline in service store
          this.rfqOutputService.setNewRFQDeadline(
            result.dialogData.rfqNumber,
            result.dialogForm.newRFQDeadline
          );
        }
      });
  }

  async onDownloadMultiple(uploadPaths: string[]) {
    if (uploadPaths.length === 0) {
      this.openSnackBar(
        'Error encountered while downloading the generated RFQs, file paths are missing.',
        'error'
      );
    }

    await this.fileService.downloadFiles(uploadPaths);
  }

  async onDownloadSingle(
    MPDocumentNumber: string,
    VendorNumber: string,
    RFQNumber: string,
    Version: number
  ) {
    const body: BodyGetRFQDocument = {
      MPDocumentNumber,
      VendorNumber,
      RFQNumber,
      Version,
    };

    try {
      const result: PrintDocument = await lastValueFrom(
        this.backendApi.getRFQDocument(body).pipe(takeUntil(this.destroy$))
      );
      const arrayBuffer: any = result.Document.data;
      const int8Array = new Uint8Array(arrayBuffer);
      let file = new Blob([int8Array], { type: 'application/pdf' });
      var fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      this.openSnackBar('RFQ print failed to send.', 'error');
    }
  }

  returnToRFQ = (): void => {
    this.router.navigateByUrl(
      '/purchase/' + this.purchaseType + '/' + this.MPDocumentNumber+';edited=true'
    );
  };

  openSnackBar(notifMsg: string, type: string = 'success') {
    this._snackBar.open(notifMsg, undefined, {
      verticalPosition: 'top',
      panelClass:
        type == 'success' ? 'snackbar-popup' : 'app-notification-error',
      duration: 5000,
    });
  }

  async loadGetPurchaseExecutivesList() {
    if (this.loggedInUser) {
      let data: PurchaseExecutives[];
      data = await lastValueFrom(this.backendApi.getPurchaseExecutivesList(this.loggedInUser));
      this.isUserPE = data.length >= 1 ? true : false;
    }
  }

  public get alertType(): typeof AlertType {
    return AlertType;
  }
}
